import {ChangeEvent, useCallback, useEffect, useImperativeHandle, useMemo, useReducer, useRef, useState} from "react";
import isEqual from "react-fast-compare";
import {ErrorsShape, extractEvent, IsTEqual, trace} from "@/lib/utils";
import {isEmpty, setField} from "@/lib/accessor";
import {displayableMessage} from "@/lib/message-key";
import {useSnackbarEx} from "@/lib/snackbarex";
import {RequestParams} from "@/api/http-client";
import {AxiosError, AxiosResponse, isAxiosError} from "axios";
import {startTimer} from "@/lib/timer";
import {SelectChangeEvent} from "@mui/material";
import {useTranslation} from "react-i18next";
import {ObjectSchema, ValidationError} from "yup";
import {SnackbarKey} from "notistack";
import {CommonI18N} from "@/translations";
import {ErrorDTO} from "@/api/data-contracts";

// yup no longer exports AnyObject and Maybe as named exports — define locally.
// AnyObject uses `any` to match yup's permissive index-signature semantics.
type AnyObject = Record<string, any>; // any is intentional: matches yup's AnyObject contract
type Maybe<T> = T | null | undefined;

export type NamedValue = {
    name: string;
    value: any;
};

export type ChangeHandler = (e: ChangeEvent | NamedValue | SelectChangeEvent<string>) => void;

export type NamedValueHandler = (nv: NamedValue) => void;

export type ModelAction = {
    type: string;
    data?: any;
    nochange?: boolean;
};

export type ValidatorOptions<M> = {
    action?: ModelAction;
};

export type ModelStateParams<M extends Maybe<AnyObject>, ID, S = M> = {
    initialState?: M;
    id?: ID | null;
    reducer?: (state: M, action: ModelAction) => M;
    loader?: (id: ID, params?: RequestParams | undefined) => Promise<AxiosResponse<M>>;
    saver?: (state: S, params?: RequestParams | undefined) => Promise<AxiosResponse<M | void>>;
    updater?: (id: ID, state: S, params?: RequestParams | undefined) => Promise<AxiosResponse<M | void>>;
    creator?: (state: S, params?: RequestParams | undefined) => Promise<AxiosResponse<M | void>>;
    schema?: ObjectSchema<M>;
    context?: () => any;
    validateDisabled?: boolean;
    validator?: (model: M, options: ValidatorOptions<M>) => Promise<ErrorsShape<M>>;
    onLoadError?: (error: AxiosError) => boolean;
    sendTransformer?: (data: M) => S;
};

export type ModelStateWithS<M, S> = {
    sendTransformer: (data: M) => S;
};

export type ModelState = "init" | "loading" | "saving" | "error" | "edit";

/** Result of normalizing an arbitrary error shape into form state plus snackbar content. */
export type NormalizedErrors<M> = {
    /** Field errors to store in the form state — empty when the error has no field mapping. */
    errorObj: ErrorsShape<M>;
    /** Messages listed below the snackbar summary. */
    messages: string[];
    /** Snackbar summary; the caller falls back to the generic validation text when unset. */
    title?: string;
    /** The error to hand to the snackbar — the unwrapped `ErrorDTO` for Axios errors. */
    error: ValidationError | ErrorDTO | ErrorsShape<M>;
};

/** True for a plain object — the only shape a caller-supplied field-error tree can have. */
const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Normalizes every error shape a form can receive into field errors and snackbar content.
 *
 * Returns `null` for 401 and 403 responses: the session handling shows its own message, so the
 * form must neither set an error state nor raise a snackbar for them.
 */
export const normalizeFormErrors = <M>(
    error: ValidationError | ErrorsShape<M> | ErrorDTO | AxiosError
): NormalizedErrors<M> | null => {
    let normalized: ValidationError | ErrorsShape<M> | ErrorDTO = error;
    // A value derived from an Axios error never describes form fields, so it must not reach the
    // pass-through branch below — neither a request that never got a response (no status) nor a
    // response body without the BoxHub error envelope.
    let isServerError = false;
    if (isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 401 || status === 403) {
            return null;
        }
        const body = error.response?.data;
        const errorDTO = isPlainObject(body) && isPlainObject(body.error) ? (body.error as ErrorDTO) : undefined;
        // Without the BoxHub envelope only the status is carried over: the Axios message is raw
        // English technical text ("Network Error"), so the caller falls back to the translated
        // generic validation text instead.
        normalized = errorDTO ?? {status: error.status};
        isServerError = true;
    }

    let errorObj: ErrorsShape<M> = {};
    const messages: string[] = [];

    if (normalized instanceof ValidationError) {
        for (const err of normalized.inner) {
            errorObj = setField(errorObj, `${err.path}.message`, err.message);
            messages.push(err.message);
        }
        return {errorObj, messages, error: normalized};
    }

    const dto = normalized as ErrorDTO;
    if (dto.status != null && dto.errors != null) {
        const fieldErrors = dto.errors;
        for (const key of Object.keys(fieldErrors)) {
            // An unresolved message key would expose internal key and constraint names. The field
            // still gets an error entry so it stays marked, only without the technical text.
            const text = displayableMessage(fieldErrors[key]) ?? "";
            errorObj = setField(errorObj, `${key}.message`, text);
            if (text !== "") {
                messages.push(text);
            }
        }
        return {errorObj, messages, title: displayableMessage(dto.message), error: normalized};
    }

    if (isServerError || typeof dto.status === "number") {
        // Server error without a field map: report the server message itself instead of writing
        // the error envelope into the form state as pseudo field errors. A numeric status alone
        // already identifies a server response — even without a message it never names a field.
        return {errorObj, messages, title: displayableMessage(dto.message), error: normalized};
    }

    if (isPlainObject(normalized)) {
        // Field error tree handed in directly by a caller.
        return {errorObj: normalized as ErrorsShape<M>, messages, error: normalized};
    }

    return {errorObj, messages, error: normalized};
};

type Api<M extends Maybe<AnyObject>, ID, S = M> = ModelStateParams<M, ID, S>;

export const useModelState = <M extends Maybe<AnyObject> = any, ID = any, S = M>({
                                                                                     initialState = {} as M,
                                                                                     id,
                                                                                     reducer,
                                                                                     loader,
                                                                                     saver,
                                                                                     updater,
                                                                                     creator,
                                                                                     schema,
                                                                                     context,
                                                                                     onLoadError,
                                                                                     validateDisabled,
                                                                                     validator,
                                                                                     sendTransformer
                                                                                 }: ModelStateParams<M, ID, S> & (IsTEqual<M, S> extends true ? {} : ModelStateWithS<M, S>)) => {
    const [status, setStatus] = useState<ModelState>("init");
    const originalRef = useRef<M>(initialState ?? ({} as M));

    const [errors, setErrorsInt] = useState<ErrorsShape<M>>({});
    const [httpErrorStatus, setHttpErrorStatus] = useState<number | undefined>(undefined);

    const validateTimerRef = useRef<number>(undefined);

    const {t} = useTranslation();
    const {showMessage} = useSnackbarEx();
    const snackbarKeyRef = useRef<SnackbarKey>(undefined);
    const validateCallRef = useRef<number>(1);

    const apiRef = useRef<Api<M, ID, S>>({
        loader,
        saver,
        updater,
        creator,
        id,
        schema,
        context,
        sendTransformer,
        validator
    });

    const setErrors = useCallback(
        (error: ValidationError | ErrorsShape<M> | ErrorDTO | AxiosError, silent?: boolean) => {
            if (isEmpty(error)) {
                setErrorsInt((le: any) => (isEmpty(le) ? le : {}));
                return;
            }

            const normalized = normalizeFormErrors<M>(error);
            if (normalized == null) {
                return;
            }
            const {errorObj, messages, title} = normalized;

            setErrorsInt((le: any) => (isEmpty(le) && isEmpty(errorObj) ? le : errorObj));
            if (!silent) {
                snackbarKeyRef.current = showMessage({
                    summary: title ?? t(CommonI18N.message.validationError),
                    list: messages,
                    severity: "error",
                    error: normalized.error,
                    life: 10000,
                    snackbarKey: snackbarKeyRef.current
                });
            }
        },
        [showMessage, t]
    );

    const validate = useCallback(
        (options?: { silent?: boolean; fields?: (keyof M)[]; action?: ModelAction }) => {
            // If context indicates disabled then skip all validations
            if (apiRef.current?.validateDisabled === true) {
                setErrors({});
                return true;
            }
            const e0 = {};
            let err: any = e0;
            if (apiRef.current?.schema) {
                const context = apiRef.current.context ? apiRef.current.context() : undefined;
                let schemaToUse: ObjectSchema<any> = apiRef.current.schema;
                if (options?.fields || context?.schemaFields) {
                    schemaToUse = schemaToUse.pick(options?.fields ?? context?.schemaFields);
                }
                try {
                    schemaToUse.validateSync(modelRef.current, {
                        abortEarly: false,
                        disableStackTrace: true,
                        context
                    });
                } catch (error) {
                    // Set error from caught ValidationError to ensure proper nested structure
                    if (error instanceof ValidationError) {
                        err = error;
                    }
                }
            }

            setErrors(err, options?.silent);
            const sync = validateCallRef.current + 1;
            validateCallRef.current = sync;

            const validatorFn = apiRef.current?.validator;
            if (validatorFn != null) {
                const runAsyncValidator = async () => {
                    try {
                        const r = await validatorFn(modelRef.current, {
                            action: options?.action
                            // evtl err
                        });
                        if (!isEmpty(r) && validateCallRef.current === sync) {
                            setErrorsInt((old) => ({...old, ...r}));
                        }
                    } catch (error: unknown) {
                        // Do not silently swallow validator rejections (e.g. a network error from an
                        // async cross-field validator). The sync error state was already applied above.
                        console.error("Async validator failed", error);
                    }
                };
                void runAsyncValidator();
            }

            return err == e0; //TODO doof, besser async / per Promise?
        },
        [setErrors]
    );

    const internal = useCallback(
        (last: M, action: ModelAction) => {
            const dispatch = (m: M) => {
                switch (action.type) {
                    case "--clear":
                        return apiRef.current.initialState;
                    case "--reset":
                        return originalRef.current;
                    case "--set":
                        return action.data;
                    case "--update": {
                        return typeof action.data === "function" ? action.data(m) : {...m, ...action.data};
                    }
                    case "--change": {
                        const next = setField(m, action.data[0], action.data[1]);
                        if (isEqual(next, m)) {
                            return m;
                        }
                        if (reducer) {
                            const probe = reducer(next, {type: "--onchange", data: action.data});
                            if (probe != null) {
                                return probe;
                            }
                        }
                        return next;
                    }
                    default: {
                        if (reducer != null) {
                            const probe = reducer(m, action);
                            if (probe != null) {
                                return probe;
                            }
                        }
                        return m;
                    }
                }
            };
            const next = dispatch(last);
            if (isEqual(next, last)) {
                return last;
            }

            trace("model-state", {action, next, last});

            if (action.nochange) {
                originalRef.current = dispatch(originalRef.current);
            } else if (apiRef.current?.schema) {
                startTimer(validateTimerRef, 250, () => {
                    validate({silent: true, action});
                });
            }
            return next;
        },
        [reducer, validate]
    );

    const [model, dispatch] = useReducer<M, any>(internal, initialState ?? ({} as M));
    const modelRef = useRef<M>(model);
    modelRef.current = model;

    const isDirty = useMemo(() => !isEqual(originalRef.current, model), [model]);

    const onChange = useCallback((e: ChangeEvent | NamedValue | SelectChangeEvent<string>) => {
        const nv = extractEvent(e);
        if (nv) {
            dispatch({type: "--change", data: nv});
        }
    }, []);

    const getModel = useCallback(() => modelRef.current, []);

    const load = useCallback(
        ({silent = true, initial = false}: { silent?: boolean; initial?: boolean } = {}) => {
            if (apiRef.current.loader && id != null) {
                setStatus("loading");
                apiRef.current
                    .loader(id)
                    .then((response) => {
                        dispatch({type: "--set", data: response.data});
                        originalRef.current = response.data;
                        setStatus("edit");
                        setErrors({});
                        setHttpErrorStatus(undefined);
                        // }
                        if (!silent) {
                            snackbarKeyRef.current = showMessage({
                                summary: t(CommonI18N.message.loaded),
                                severity: "info",
                                snackbarKey: snackbarKeyRef.current
                            });
                        }
                    })
                    .catch((error) => {
                        setStatus("error");
                        if (isAxiosError(error)) {
                            setHttpErrorStatus(error.response?.status);
                        } else {
                            setHttpErrorStatus(undefined);
                        }
                        if (!apiRef.current.onLoadError?.(error)) {
                            snackbarKeyRef.current = showMessage({
                                summary: t(CommonI18N.message.loadFailed),
                                severity: "error",
                                error,
                                snackbarKey: snackbarKeyRef.current
                            });
                        }
                    });
            } else {
                setTimeout(() => {
                    // On the initial mount-triggered load the reducer already started with
                    // initialState, so re-dispatching it here is redundant — and destructive for
                    // anything the user typed during the first 100ms (autofill, fast input): the
                    // deferred "--set" would silently replace the edited model with the pristine
                    // initialState. Only a manual load() call keeps the reset-to-initial semantics.
                    if (!initial) {
                        const data = apiRef.current?.initialState ?? ({} as M);
                        dispatch({type: "--set", data});
                        originalRef.current = data;
                        setErrors({});
                    }
                    setStatus("edit");
                    if (!silent) {
                        snackbarKeyRef.current = showMessage({
                            summary: t(CommonI18N.message.loaded),
                            severity: "info",
                            snackbarKey: snackbarKeyRef.current
                        });
                    }
                }, 100);
            }
        },
        [id, setErrors, showMessage, t]
    );

    const save = useCallback(
        ({successMsg}: { successMsg?: string } = {}): Promise<AxiosResponse<M | void>> | null => {
            if (!validate()) {
                return null;
            }
            let promise: Promise<AxiosResponse<M | void>> | null = null;
            if (apiRef.current.id != null) {
                if (apiRef.current.updater) {
                    promise = apiRef.current.updater(
                        apiRef.current.id,
                        apiRef.current.sendTransformer!(modelRef.current)
                    );
                } else if (apiRef.current.saver) {
                    promise = apiRef.current.saver(apiRef.current.sendTransformer!(modelRef.current));
                }
            } else if (apiRef.current.creator) {
                promise = apiRef.current.creator(apiRef.current.sendTransformer!(modelRef.current));
            } else if (apiRef.current.saver) {
                promise = apiRef.current.saver(apiRef.current.sendTransformer!(modelRef.current));
            }
            if (promise) {
                setStatus("saving");
                promise = promise
                    .then((response) => {
                        if (response.data) {
                            originalRef.current = response.data;
                            dispatch({type: "--set", data: response.data});
                        }
                        setStatus("edit");
                        snackbarKeyRef.current = showMessage({
                            summary: successMsg ?? t(CommonI18N.message.saved),
                            severity: "info",
                            snackbarKey: snackbarKeyRef.current
                        });
                        return response;
                    })
                    .catch((error) => {
                        setStatus("edit");
                        // Write the server's field map into the form state so the affected inputs are
                        // highlighted — without this the response's `error.errors` was dropped and only the
                        // snackbar reported the failure. Silent on purpose: the snackbar below already
                        // reports it, and a second one would replace the summary with the generic
                        // validation text. A response without a field map leaves the state untouched, and
                        // 401/403 are skipped entirely (see normalizeFormErrors).
                        setErrors(error, true);
                        snackbarKeyRef.current = showMessage({
                            summary: t(CommonI18N.message.saveFailed),
                            severity: "error",
                            error,
                            snackbarKey: snackbarKeyRef.current
                        });
                        throw error;
                    });
            }
            return promise;
        },
        [setErrors, showMessage, t, validate]
    );

    useImperativeHandle(
        apiRef,
        () => ({
            initialState,
            loader,
            saver,
            updater,
            creator,
            id,
            schema,
            context,
            onLoadError,
            validateDisabled,
            validator,
            sendTransformer: sendTransformer ?? ((d) => d as any)
        }),
        [
            initialState,
            loader,
            saver,
            updater,
            creator,
            id,
            schema,
            context,
            onLoadError,
            validateDisabled,
            validator,
            sendTransformer
        ]
    );

    useEffect(() => {
        trace("model-state", {errors});
    }, [errors]);

    const lastLoadedIdRef = useRef(id);

    // `load` changes identity whenever `t` or `showMessage` does — i.e. on every app language
    // switch. Reading it through a ref keeps the effect below tied to `id` alone: a language switch
    // must never re-run the loader, because the response replaces the whole model and silently
    // discards what the user has typed so far. Updated via useImperativeHandle so the ref is current
    // before the effect runs (layout effects execute first).
    const loadRef = useRef(load);
    useImperativeHandle(loadRef, () => load, [load]);

    useEffect(() => {
        // "initial" (skip the redundant reset — see load()) applies to the first mount. A REAL id
        // change while mounted must keep the pre-existing reset semantics: the form now represents a
        // different record, so the model is re-initialized from the current initialState. Comparing
        // against the last loaded id (instead of a first-run flag) keeps this StrictMode-safe — a
        // double-invoked mount effect sees the same id twice.
        const idChanged = lastLoadedIdRef.current !== id;
        lastLoadedIdRef.current = id;
        loadRef.current({silent: true, initial: !idChanged});
    }, [id]);

    useEffect(() => {
        if (validateDisabled !== true) {
            validate({silent: true});
        }
    }, [validate, validateDisabled]);

    const original = originalRef.current;

    const hasErrors = useMemo(() => !isEmpty(errors), [errors]);

    return {
        model,
        original,
        errors,
        httpErrorStatus,
        hasErrors,
        status,
        isDirty,
        dispatch,
        onChange,
        getModel,
        validate,
        save,
        load,
        setErrors
    };
};
